import type { ReactNode } from 'react';

type Tone = 'ok' | 'pending' | 'flag' | 'quiet';

interface StampProps {
  children: ReactNode;
  tone?: Tone;
  /** Upright, for dense lists where the tilt would fight the alignment. */
  flat?: boolean;
  title?: string;
}

/**
 * The one loud element in the interface. It marks state that used to be a
 * rubber stamp on paper: vaccines current, agreement signed, deposit received.
 * It is never decorative, so if there is nothing true to stamp, nothing shows.
 */
export function Stamp({ children, tone = 'quiet', flat = false, title }: StampProps) {
  return (
    <span className={`stamp stamp-${tone}${flat ? ' stamp-flat' : ''}`} title={title}>
      {children}
    </span>
  );
}

const BOOKING_STAMPS: Record<string, { label: string; tone: Tone }> = {
  requested: { label: 'Requested', tone: 'pending' },
  awaiting_documents: { label: 'Needs paperwork', tone: 'flag' },
  awaiting_payment: { label: 'Awaiting payment', tone: 'flag' },
  confirmed: { label: 'Confirmed', tone: 'ok' },
  in_progress: { label: 'In care', tone: 'ok' },
  completed: { label: 'Completed', tone: 'quiet' },
  cancelled: { label: 'Cancelled', tone: 'quiet' },
  declined: { label: 'Declined', tone: 'flag' },
};

export function BookingStamp({ status, flat }: { status: string; flat?: boolean }) {
  const stamp = BOOKING_STAMPS[status] ?? { label: status, tone: 'quiet' as Tone };
  return (
    <Stamp tone={stamp.tone} flat={flat}>
      {stamp.label}
    </Stamp>
  );
}

const PAYMENT_STAMPS: Record<string, { label: string; tone: Tone }> = {
  unpaid: { label: 'Unpaid', tone: 'flag' },
  deposit_paid: { label: 'Deposit received', tone: 'pending' },
  paid: { label: 'Paid in full', tone: 'ok' },
  refunded: { label: 'Refunded', tone: 'quiet' },
  partially_refunded: { label: 'Part refunded', tone: 'quiet' },
};

export function PaymentStamp({ status, flat }: { status: string; flat?: boolean }) {
  const stamp = PAYMENT_STAMPS[status] ?? { label: status, tone: 'quiet' as Tone };
  return (
    <Stamp tone={stamp.tone} flat={flat}>
      {stamp.label}
    </Stamp>
  );
}

const INVOICE_STAMPS: Record<string, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'quiet' },
  sent: { label: 'Sent', tone: 'pending' },
  paid: { label: 'Paid', tone: 'ok' },
  void: { label: 'Void', tone: 'quiet' },
};

export function InvoiceStamp({ status, flat }: { status: string; flat?: boolean }) {
  const stamp = INVOICE_STAMPS[status] ?? { label: status, tone: 'quiet' as Tone };
  return (
    <Stamp tone={stamp.tone} flat={flat}>
      {stamp.label}
    </Stamp>
  );
}

export function VaccinationStamp({ current, flat }: { current: boolean; flat?: boolean }) {
  return current ? (
    <Stamp tone="ok" flat={flat}>
      Vaccines current
    </Stamp>
  ) : (
    <Stamp tone="flag" flat={flat}>
      Vaccines needed
    </Stamp>
  );
}

const ASSESSMENT_STAMPS: Record<string, { label: string; tone: Tone }> = {
  scheduled: { label: 'Assessment scheduled', tone: 'pending' },
  passed: { label: 'Assessment passed', tone: 'ok' },
  failed: { label: 'Assessment failed', tone: 'flag' },
};

/** `not_started` is not a real state to stamp, so it renders nothing. */
export function AssessmentStamp({ status, flat }: { status: string; flat?: boolean }) {
  const stamp = ASSESSMENT_STAMPS[status];
  if (!stamp) return null;
  return (
    <Stamp tone={stamp.tone} flat={flat}>
      {stamp.label}
    </Stamp>
  );
}
